import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Grid,
  Alert,
  CircularProgress,
  Chip,
} from '@mui/material';
import { LocalShipping, LocationOn } from '@mui/icons-material';
import { api } from '../services/api';

interface DeliveryArea {
  postcode: number;
  suburb: string;
  direction: string;
}

interface DeliveryZone {
  zone: string;
  fee: number;
  areas: DeliveryArea[];
}

const DeliveryFeesPage: React.FC = () => {
  const [zones, setZones] = useState<DeliveryZone[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDeliveryFees = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get('/v1/delivery-fees');
        setZones(response.data.data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    fetchDeliveryFees();
  }, []);

  const getZoneColor = (zone: string) => {
    const zoneColors: { [key: string]: string } = {
      'Zone 1': '#f5f5dc', // Light beige
      'Zone 2': '#e8f5e8', // Light green
      'Zone 3': '#e3f2fd', // Light blue
      'Zone 4': '#fff8e1', // Light yellow
      'Zone 5': '#fce4ec', // Light pink
    };
    return zoneColors[zone] || '#f5f5f5';
  };

  const groupAreasByPostcode = (areas: DeliveryArea[]) => {
    const grouped: { [key: number]: string[] } = {};
    areas.forEach(area => {
      if (!grouped[area.postcode]) {
        grouped[area.postcode] = [];
      }
      grouped[area.postcode].push(area.suburb);
    });
    return grouped;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 6, textAlign: 'center' }}>
        <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          Delivery Fee List
        </Typography>
        <Typography variant="h5" color="text.secondary" paragraph sx={{ maxWidth: 800, mx: 'auto' }}>
          Find your delivery fee based on your postcode and suburb
        </Typography>
      </Box>

      {/* Delivery Zones */}
      <Grid container spacing={3}>
        {zones.map((zone) => {
          const groupedAreas = groupAreasByPostcode(zone.areas);
          return (
            <Grid item xs={12} md={6} lg={4} key={zone.zone}>
              <Card
                sx={{
                  height: '100%',
                  backgroundColor: getZoneColor(zone.zone),
                  border: '2px solid',
                  borderColor: 'primary.main',
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Box display="flex" alignItems="center" gap={2} mb={2}>
                    <LocalShipping sx={{ fontSize: 32, color: 'primary.main' }} />
                    <Box>
                      <Typography variant="h5" component="h2" sx={{ fontWeight: 'bold' }}>
                        {zone.zone}
                      </Typography>
                      <Typography variant="h4" color="primary.main" sx={{ fontWeight: 'bold' }}>
                        ${zone.fee}
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', mb: 2 }}>
                    Post Codes & Suburbs:
                  </Typography>
                  
                  <Box sx={{ maxHeight: '400px', overflowY: 'auto' }}>
                    {Object.entries(groupedAreas).map(([postcode, suburbs]) => (
                      <Box key={postcode} sx={{ mb: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                          {postcode}:
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, ml: 2 }}>
                          {suburbs.map((suburb, index) => (
                            <Chip
                              key={`${postcode}-${suburb}-${index}`}
                              label={suburb}
                              size="small"
                              variant="outlined"
                              sx={{ fontSize: '0.75rem' }}
                            />
                          ))}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Additional Information */}
      <Box sx={{ mt: 6 }}>
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            <LocationOn sx={{ mr: 1, verticalAlign: 'middle' }} />
            Important Delivery Information
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>Delivery Times:</strong> Deliveries are made between 8:00 AM and 2:30 PM on your selected food parcel day.
          </Typography>
          <Typography variant="body1" paragraph>
            <strong>Phone Availability:</strong> You must have your phone on from 7:00 AM for delivery drivers, 
            otherwise your parcel will be cancelled and no further parcels unless a $20 repacking fee is paid.
          </Typography>
          <Typography variant="body1">
            <strong>Contact:</strong> If you have any questions about delivery fees or need assistance, 
            please call <strong>0410 565 781</strong>.
          </Typography>
        </Alert>
      </Box>
    </Container>
  );
};

export default DeliveryFeesPage;
